import { IApiService, IQuoteService, ITpaService, IUserService, User, PipelineMetrics, SoldCasesData, EnrollmentsData } from './interfaces/IApiService';
import { Auth } from 'aws-amplify';
import { getEnvironment, configs } from '../config/environment';
import { Organization, NewOrganization } from '../types/organization';

const getApiHeaders = async (contentType = 'application/json'): Promise<HeadersInit> => {
  const environment = getEnvironment();
  const envConfig = configs[environment];

  const headers: HeadersInit = {
    'Content-Type': contentType,
  };

  if (envConfig.apiKey) {
    headers['x-api-key'] = envConfig.apiKey;
  }

  if (envConfig.authEnabled) {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      headers['Authorization'] = `Bearer ${token}`;
      console.log('Added Authorization header with JWT token');
    } catch (error) {
      console.warn('No JWT token available for request:', error);
    }
  }

  return headers;
};

class ProductionQuoteService implements IQuoteService {
  private async getApiUrl(): Promise<string> {
    const environment = getEnvironment();
    const envConfig = configs[environment];
    return envConfig.apiUrl;
  }

  async getQuotes(tpaId?: string): Promise<any[]> {
    try {
      const apiUrl = await this.getApiUrl();
      const url = tpaId
        ? `${apiUrl}/api/quotes?tpaId=${encodeURIComponent(tpaId)}`
        : `${apiUrl}/api/quotes`;

      const headers = await getApiHeaders();
      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching quotes:', error);
      throw error;
    }
  }

  async getQuoteById(id: string, brokerId?: string, employerId?: string): Promise<any | null> {
    try {
      const apiUrl = await this.getApiUrl();
      const headers = await getApiHeaders();

      let url = `${apiUrl}/api/quotes/${id}`;
      const params = new URLSearchParams();
      if (brokerId) params.append('brokerId', brokerId);
      if (employerId) params.append('employerId', employerId);
      if (params.toString()) url += `?${params.toString()}`;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching quote ${id}:`, error);
      throw error;
    }
  }

  async submitQuote(quoteData: any): Promise<any> {
    try {
      console.log('ProductionQuoteService.submitQuote called with data:', quoteData);

      const apiUrl = await this.getApiUrl();
      const environment = getEnvironment();
      const envConfig = configs[environment];

      let authToken = quoteData.authToken;
      if (!authToken && envConfig.authEnabled) {
        console.log('No token provided in quoteData, retrieving from Cognito...');
        try {
          const session = await Auth.currentSession();
          authToken = session.getIdToken().getJwtToken();
          console.log('Successfully retrieved auth token at the beginning of submitQuote');
          quoteData.authToken = authToken;
        } catch (tokenError) {
          console.error('Failed to get token at the beginning of submitQuote:', tokenError);
          throw new Error('Authentication required. Please sign in again.');
        }
      }

      console.log('Using API endpoint:', `${apiUrl}/quotes`);

      if (authToken) {
        delete quoteData.authToken;
      }

      const hasFiles = (quoteData.censusFile && ((Array.isArray(quoteData.censusFile) && quoteData.censusFile.length > 0) || quoteData.censusFile instanceof File)) ||
                       (quoteData.planComparisonFile && ((Array.isArray(quoteData.planComparisonFile) && quoteData.planComparisonFile.length > 0) || quoteData.planComparisonFile instanceof File));
      console.log('Has files:', hasFiles);

      let response;

      if (hasFiles) {
        const formData = new FormData();

        Object.keys(quoteData).forEach(key => {
          if (quoteData[key] !== null && quoteData[key] !== undefined) {
            if (key === 'censusFile' || key === 'planComparisonFile') {
              const files = quoteData[key];
              if (Array.isArray(files)) {
                files.forEach((file: File) => {
                  if (file instanceof File) {
                    formData.append(key, file);
                    console.log(`Added file to form data: ${key}, name: ${file.name}, size: ${file.size} bytes`);
                  }
                });
              } else if (files instanceof File) {
                formData.append(key, files);
                console.log(`Added single file to form data: ${key}, name: ${files.name}, size: ${files.size} bytes`);
              }
            } else if (key !== 'authToken') {
              formData.append(key, String(quoteData[key]));
            }
          }
        });

        console.log('Sending FormData request...');

        try {
          let headers: HeadersInit;
          if (authToken) {
            headers = {
              'x-api-key': envConfig.apiKey || '',
              'Authorization': `Bearer ${authToken}`
            };
            console.log('Using provided auth token for FormData request');
          } else {
            headers = await getApiHeaders();
            console.log('Using getApiHeaders for FormData request');
          }

          response = await fetch(`${apiUrl}/quotes`, {
            method: 'POST',
            headers,
            body: formData
          });
        } catch (error) {
          console.error('Error in FormData request:', error);
          throw error;
        }
      } else {
        console.log('Sending JSON request...');

        try {
          let headers: HeadersInit;
          if (authToken) {
            headers = {
              'Content-Type': 'application/json',
              'x-api-key': envConfig.apiKey || '',
              'Authorization': `Bearer ${authToken}`
            };
            console.log('Using provided auth token for JSON request');
          } else {
            headers = await getApiHeaders();
            (headers as Record<string, string>)['Content-Type'] = 'application/json';
            console.log('Using getApiHeaders for JSON request');
          }

          const { authToken: _, ...dataToSend } = quoteData;

          response = await fetch(`${apiUrl}/quotes`, {
            method: 'POST',
            headers,
            body: JSON.stringify(dataToSend)
          });
        } catch (error) {
          console.error('Error in JSON request:', error);
          throw error;
        }
      }

      console.log('API response status:', response.status);

      const headers: Record<string, string> = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });
      console.log('API response headers:', headers);

      const contentType = response.headers.get('content-type');
      let responseData;

      if (contentType && contentType.includes('application/json')) {
        try {
          responseData = await response.json();
          console.log('API response data (JSON):', responseData);
        } catch (jsonError) {
          console.error('Error parsing JSON response:', jsonError);
          responseData = { error: 'Failed to parse JSON response' };
        }
      } else {
        try {
          responseData = await response.text();
          console.log('API response data (text):', responseData.substring(0, 200));
          if (responseData.length > 200) {
            console.log('... (truncated)');
          }
        } catch (textError) {
          console.error('Error reading response text:', textError);
          responseData = 'Failed to read response';
        }
      }

      if (!response.ok) {
        throw new Error(
          responseData?.message ||
          responseData?.error ||
          `API error: ${response.status} - ${response.statusText}`
        );
      }

      return responseData;
    } catch (error) {
      console.error('Error in ProductionQuoteService.submitQuote:', error);
      if (error instanceof Error) {
        console.error('Error stack trace:', error.stack);
      }
      throw error;
    }
  }

  async updateQuoteStatus(id: string, status: string, brokerId: string, employerId: string): Promise<any> {
    try {
      console.log('ProductionQuoteService.updateQuoteStatus:', id, 'to', status);

      const apiUrl = await this.getApiUrl();
      const headers = await getApiHeaders();

      const url = `${apiUrl}/api/quotes/${id}?brokerId=${brokerId}&employerId=${employerId}`;
      console.log('Making PUT request to:', url);

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating quote status:', error);
      throw error;
    }
  }
}

class ProductionTpaService implements ITpaService {
  private async getAdminApiUrl(): Promise<string> {
    const environment = getEnvironment();
    const envConfig = configs[environment];
    return envConfig.adminApiUrl || envConfig.apiUrl;
  }

  async getTpaData(): Promise<any[]> {
    try {
      console.log('ProductionTpaService.getTpaData: Fetching TPA data');

      const headers = await getApiHeaders();
      const adminApiUrl = await this.getAdminApiUrl();

      const timestamp = new Date().getTime();
      const url = `${adminApiUrl}/api/tpa?_t=${timestamp}`;

      console.log(`ProductionTpaService.getTpaData: Making request to ${url}`);

      const response = await fetch(url, {
        headers,
        credentials: 'omit',
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      console.log('ProductionTpaService.getTpaData: Data received:', data);

      return data;
    } catch (error) {
      console.error('ProductionTpaService.getTpaData: Error fetching TPA data:', error);
      throw error;
    }
  }

  async getTpaById(id: string): Promise<any | null> {
    try {
      const adminApiUrl = await this.getAdminApiUrl();
      const headers = await getApiHeaders();
      const response = await fetch(`${adminApiUrl}/api/tpa/${id}`, { headers });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error(`Error fetching TPA ${id}:`, error);
      throw error;
    }
  }
}

class ProductionUserService implements IUserService {
  private async getAdminApiUrl(): Promise<string> {
    const environment = getEnvironment();
    const envConfig = configs[environment];
    return envConfig.adminApiUrl || envConfig.apiUrl;
  }

  async getUsers(tpaId?: string): Promise<User[]> {
    try {
      console.log('ProductionUserService: Getting users', tpaId ? `for TPA ${tpaId}` : '');

      const adminApiUrl = await this.getAdminApiUrl();
      const headers = await getApiHeaders();

      const url = tpaId
        ? `${adminApiUrl}/api/users?tpaId=${encodeURIComponent(tpaId)}`
        : `${adminApiUrl}/api/users`;

      const response = await fetch(url, { headers });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('ProductionUserService: Error fetching users:', error);
      throw error;
    }
  }

  async createUser(userData: any): Promise<User> {
    try {
      console.log('ProductionUserService: Creating user:', userData);

      const adminApiUrl = await this.getAdminApiUrl();
      const headers = await getApiHeaders();

      const response = await fetch(`${adminApiUrl}/api/users`, {
        method: 'POST',
        headers,
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('ProductionUserService: Error creating user:', error);
      throw error;
    }
  }

  async updateUser(username: string, userData: any): Promise<User> {
    try {
      console.log('ProductionUserService: Updating user:', username, userData);

      const adminApiUrl = await this.getAdminApiUrl();
      const headers = await getApiHeaders();

      const response = await fetch(`${adminApiUrl}/api/users/${encodeURIComponent(username)}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('ProductionUserService: Error updating user:', error);
      throw error;
    }
  }

  async deleteUser(username: string): Promise<void> {
    try {
      console.log('ProductionUserService: Deleting user:', username);

      const adminApiUrl = await this.getAdminApiUrl();
      const headers = await getApiHeaders();

      const response = await fetch(`${adminApiUrl}/api/users/${encodeURIComponent(username)}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }
    } catch (error) {
      console.error('ProductionUserService: Error deleting user:', error);
      throw error;
    }
  }

  async enableUser(username: string): Promise<void> {
    try {
      console.log('ProductionUserService: Enabling user:', username);

      const adminApiUrl = await this.getAdminApiUrl();
      const headers = await getApiHeaders();

      const response = await fetch(`${adminApiUrl}/api/users/${encodeURIComponent(username)}/enable`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }
    } catch (error) {
      console.error('ProductionUserService: Error enabling user:', error);
      throw error;
    }
  }

  async disableUser(username: string): Promise<void> {
    try {
      console.log('ProductionUserService: Disabling user:', username);

      const adminApiUrl = await this.getAdminApiUrl();
      const headers = await getApiHeaders();

      const response = await fetch(`${adminApiUrl}/api/users/${encodeURIComponent(username)}/disable`, {
        method: 'POST',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }
    } catch (error) {
      console.error('ProductionUserService: Error disabling user:', error);
      throw error;
    }
  }
}

export class ProductionApiService implements IApiService {
  public Quote: IQuoteService;
  public Tpa: ITpaService;
  public Users: IUserService;

  constructor() {
    this.Quote = new ProductionQuoteService();
    this.Tpa = new ProductionTpaService();
    this.Users = new ProductionUserService();
  }

  async checkApiHealth(): Promise<boolean> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const healthEndpoint = `${envConfig.apiUrl}/health`;

      console.log(`Checking API health at: ${healthEndpoint}`);

      const response = await fetch(healthEndpoint, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': envConfig.apiKey || ''
        }
      });

      console.log('API health check successful - API is accessible');
      return true;
    } catch (error) {
      console.error('API health check error:', error);
      return false;
    }
  }

  async getQuoteById(id: string, brokerId?: string, employerId?: string): Promise<any> {
    return this.Quote.getQuoteById(id, brokerId, employerId);
  }

  async submitImplementation(id: string, implementationData: any): Promise<any> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const apiUrl = envConfig.apiUrl;

      console.log('ProductionApiService: Submitting implementation for quote:', id);

      const headers = await getApiHeaders();
      const response = await fetch(`${apiUrl}/quotes/${id}/implementation`, {
        method: 'POST',
        headers,
        body: JSON.stringify(implementationData)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error submitting implementation:', error);
      throw error;
    }
  }

  async saveImplementationDraft(id: string, draftData: any): Promise<any> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const apiUrl = envConfig.apiUrl;

      console.log('ProductionApiService: Saving implementation draft for quote:', id);

      const headers = await getApiHeaders();
      const response = await fetch(`${apiUrl}/quotes/${id}/implementation/draft`, {
        method: 'POST',
        headers,
        body: JSON.stringify(draftData)
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error saving implementation draft:', error);
      throw error;
    }
  }

  async getDashboardPipeline(): Promise<PipelineMetrics> {
    console.log('ProductionApiService: Dashboard pipeline metrics not yet available from backend');
    // Return empty/placeholder data until backend supports these metrics
    return {
      activeQuotesTotal: { count: 0, membership: 0, percentALES: '0%', revenue: '$0' },
      activeQuotesSavings: { count: 0, membership: 0, percentALES: '0%', revenue: '$0' },
      closeRatios: { allTime: '0%', priorQuarter: '0%', past90: '0%' },
      highestOpps: []
    };
  }

  async getSoldCases(): Promise<SoldCasesData> {
    console.log('ProductionApiService: Sold cases data not yet available from backend');
    // Return empty/placeholder data until backend supports these metrics
    return {
      y2025: { groups: 0, expectedEnrollment: '0', annualRev: '$0', renewal: '0%' },
      y2026: { groups: 0, expectedEnrollment: '0', annualRev: '$0', renewal: '' }
    };
  }

  async getEnrollments(): Promise<EnrollmentsData> {
    console.log('ProductionApiService: Enrollments data not yet available from backend');
    // Return empty/placeholder data until backend supports these metrics
    return {
      y2025: { employees: 0, dependents: 0, total: 0, pending: 0 },
      y2026: { employees: 0, dependents: 0, total: 0 }
    };
  }

  // Organization management methods
  async getOrganizations(): Promise<Organization[]> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const apiUrl = envConfig.adminApiUrl || envConfig.apiUrl;

      console.log('ProductionApiService: Getting organizations');

      const headers = await getApiHeaders();
      const response = await fetch(`${apiUrl}/api/organizations`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return data.organizations || [];
    } catch (error) {
      console.error('Error fetching organizations:', error);
      throw error;
    }
  }

  async getOrganizationById(id: string): Promise<Organization> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const apiUrl = envConfig.adminApiUrl || envConfig.apiUrl;

      console.log('ProductionApiService: Getting organization by ID:', id);

      const headers = await getApiHeaders();
      const response = await fetch(`${apiUrl}/api/organizations/${id}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      return data.organization;
    } catch (error) {
      console.error('Error fetching organization:', error);
      throw error;
    }
  }

  async createOrganization(org: NewOrganization): Promise<Organization> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const apiUrl = envConfig.adminApiUrl || envConfig.apiUrl;

      console.log('ProductionApiService: Creating organization:', org);

      const headers = await getApiHeaders();
      const response = await fetch(`${apiUrl}/api/organizations`, {
        method: 'POST',
        headers,
        body: JSON.stringify(org)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      return data.organization;
    } catch (error) {
      console.error('Error creating organization:', error);
      throw error;
    }
  }

  async updateOrganization(id: string, updates: Partial<Organization>): Promise<Organization> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const apiUrl = envConfig.adminApiUrl || envConfig.apiUrl;

      console.log('ProductionApiService: Updating organization:', id, updates);

      const headers = await getApiHeaders();
      const response = await fetch(`${apiUrl}/api/organizations/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      const data = await response.json();
      return data.organization;
    } catch (error) {
      console.error('Error updating organization:', error);
      throw error;
    }
  }

  async deleteOrganization(id: string): Promise<void> {
    try {
      const environment = getEnvironment();
      const envConfig = configs[environment];
      const apiUrl = envConfig.adminApiUrl || envConfig.apiUrl;

      console.log('ProductionApiService: Deleting organization:', id);

      const headers = await getApiHeaders();
      const response = await fetch(`${apiUrl}/api/organizations/${id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      console.log('Organization deleted successfully');
    } catch (error) {
      console.error('Error deleting organization:', error);
      throw error;
    }
  }

  // White-label methods (not implemented for production yet)
  async getWhiteLabelConfig(): Promise<any> {
    console.warn('White-label not implemented in production mode');
    throw new Error('White-label feature not available in production');
  }

  async saveWhiteLabelConfig(config: any): Promise<any> {
    console.warn('White-label not implemented in production mode');
    throw new Error('White-label feature not available in production');
  }

  async resetWhiteLabelConfig(): Promise<any> {
    console.warn('White-label not implemented in production mode');
    throw new Error('White-label feature not available in production');
  }
}